import { createElement, useEffect, useRef, useState, type CSSProperties, type ElementType, type ReactNode } from 'react';
import { CONTACT_FORM_TYPE_LABELS } from '@trenigo/shared';
import { MoonIcon, SunIcon, setThemePreference, useEffectiveTheme } from '@trenigo/brand';
import { LandingBrandLogo } from '../components/LandingBrandLogo.js';
import { readLoginUrl } from '../lib/login-url.js';
import { LandingLogoHero } from './LandingLogoHero.js';
import { ContactSection } from './ContactSection.js';
import './LandingPage.css';

// Landing page přenesená z apps/web/src/pages/LandingPage.tsx (FR01 20261001_LandingPage/
// 002_Marketing, FR-02). Obsah, texty, sekce, kotvy a animace jsou beze změny. Rozdíly kvůli
// předrenderování (FR-03): logo je LandingBrandLogo, rok v patičce se bere z buildu a SEO hlavička
// je statická v předrenderovaném HTML (src/head.ts) místo dřívějšího useLandingSeo. Sekce „Kontakt“
// s odkazem v navigaci přibyla v FR02 20261001_LandingPage/002_Marketing (ContactSection.tsx).

// Cíl odkazů „Přihlásit se“ - https://app.trenigo.cz/login, nebo build-time VITE_APP_LOGIN_URL
// testovacího webu (src/lib/login-url.ts).
const LOGIN_URL = readLoginUrl();

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 12);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return scrolled;
}

let revealCounter = 0;

function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  const indexRef = useRef(revealCounter++);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }
    el.style.transitionDelay = `${Math.min(indexRef.current % 4, 3) * 70}ms`;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function Reveal({
  as = 'div',
  className = '',
  children,
  ...rest
}: {
  as?: ElementType;
  className?: string;
  children: ReactNode;
  [key: string]: unknown;
}) {
  const { ref, visible } = useReveal<HTMLElement>();
  return createElement(
    as,
    { ref, className: `reveal ${className} ${visible ? 'visible' : ''}`.trim(), ...rest },
    children,
  );
}

// Přepínač Světlý/Tmavý - zapisuje stejnou předvolbu jako oddíl "Vzhled" v UserMenu aplikace
// (localStorage je vázaný na origin trenigo.cz). Předrenderované HTML i hydratace vykreslí neutrální
// stav (Světlý, server snapshot useEffectiveTheme), stav podle předvolby se projeví hned po
// hydrataci. Barvy stránky přitom neproblikávají, data-theme nastavil theme-init.js (FR-04).
function ThemeToggle() {
  const isDark = useEffectiveTheme() === 'dark';
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label="Tmavý režim"
      aria-pressed={isDark}
      title={isDark ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim'}
      onClick={() => setThemePreference(isDark ? 'light' : 'dark')}
    >
      {isDark ? <SunIcon aria-hidden="true" /> : <MoonIcon aria-hidden="true" />}
    </button>
  );
}

const FEATURES = [
  { icon: '✓', title: 'Víte, kdo přišel', text: 'Trénink a docházku zapíšete z telefonu za pár vteřin. Všichni oprávnění vidí stejná aktuální data.' },
  { icon: '₵', title: 'Víte, kolik komu zbývá', text: 'Kredit se po tréninku automaticky odečte a po platbě zase připíše. Bez kalkulačky a ručních oprav.' },
  { icon: '↗', title: 'Víte, kdo zaplatil', text: 'Platby a kreditní historii najdete na jednom místě. Hned poznáte, kde je vše v pořádku a kde je třeba zareagovat.' },
  { icon: '⌘', title: 'Každý vidí právě to, co má', text: 'Hlavní trenér, vedoucí skupiny, trenér i svěřenec mají vlastní přístup a odpovídající rozsah dat.' },
  { icon: 'Kč', title: 'Mzdy bez přepisování hodin', text: 'Trenigo připraví podklady pro odměny podle skutečně uskutečněných tréninků a nastavených sazeb.' },
  { icon: 'A', title: 'Celá akademie pohromadě', text: 'Trenéři, svěřenci, ceníky, dokumenty i platby v jedné aplikaci. Data jednotlivých akademií zůstávají oddělená.' },
];

const BENEFITS = [
  { num: '1', title: 'Zápis během několika sekund', text: 'Označíte účast, stav tréninku nebo poznámku přímo na místě.' },
  { num: '2', title: 'Svěřenec má svůj přehled', text: 'Vidí své tréninky, platby a aktuální kredit. Nemusí se pokaždé ptát trenéra.' },
  { num: '3', title: 'Jedna aktuální verze dat', text: 'Tréninky, kredity, platby i mzdy na sebe navazují. Žádné rozdílné tabulky a dohledávání, která verze je ta poslední.' },
];

const STEPS = [
  { num: '01', title: 'Nastavíte akademii', text: 'Doplníte název, logo, ceník, pravidla a role trenérů.' },
  { num: '02', title: 'Přidáte trenéry a svěřence', text: 'Vytvoříte svůj tým a klientskou základnu, se kterou budete pracovat.' },
  { num: '03', title: 'Trénujete. Trenigo počítá.', text: 'Docházka aktualizuje kredit a vytváří podklady pro další agendu.' },
];

const ROLES = [
  { label: 'Individuální trenér', title: 'Méně papírování mezi tréninky.', text: 'Svěřenci, tréninky, docházka, platby a kredity bez zbytečného přepisování a hledání ve starých poznámkách.' },
  { label: 'Vedoucí skupiny', title: 'Víte, co se děje ve vašem týmu.', text: 'Mějte přehled o trenérech, svěřencích a uskutečněných trénincích bez obcházení několika evidencí.' },
  { label: 'Sportovní akademie', title: 'Celý provoz pod kontrolou.', text: 'Řízení trenérů a svěřenců, role, ceníky, platby, mzdy, dokumenty i historie změn — všechno propojené v jednom systému.' },
];

const FAQ = [
  { q: 'Musím něco instalovat?', a: 'Ne. Trenigo je webová aplikace a funguje přímo v prohlížeči — na počítači, tabletu i telefonu.' },
  { q: 'Je Trenigo vhodné i pro jednoho trenéra?', a: 'Ano. Vystačíte si se svými svěřenci, tréninky, kredity a platbami. Trenérský tým a další role využijete až ve chvíli, kdy je budete potřebovat.' },
  { q: 'Uvidí každý trenér všechna data akademie?', a: 'Ne. Přístup se řídí rolí a postavením trenéra v hierarchii. Každý pracuje pouze s daty, ke kterým má oprávnění.' },
  { q: 'Jak fungují kredity?', a: 'Kredit se po dokončeném tréninku odečte a po platbě zase připíše. Zůstatek se vždy počítá z historie plateb a tréninků, nikde se neeviduje ručně.' },
  { q: 'Dostane svěřenec vlastní přístup?', a: 'Může mít vlastní portál se svými tréninky, docházkou, platbami a aktuálním kreditem. Nemusí se pak na základní věci ptát trenéra.' },
  { q: 'Co když potřebuji opravit docházku nebo platbu?', a: 'Stornovaná platba i úprava kreditu zůstávají v historii u svěřence, takže je vždy vidět, co a kdy se změnilo.' },
];

// Rok buildu v předrenderovaném HTML i při hydrataci (shodné), aktuální rok až po hydrataci. Jinak
// by se HTML sestavené v jiném roce, než je návštěva, při hydrataci neshodovalo.
function useFooterYear() {
  const [year, setYear] = useState<number>(__LANDING_BUILD_YEAR__);
  useEffect(() => setYear(new Date().getFullYear()), []);
  return year;
}

export function LandingPage() {
  const scrolled = useScrolled();
  const year = useFooterYear();
  const [demoRequest, setDemoRequest] = useState(0);

  return (
    <div className="landing">
      <a className="skip-link" href="#obsah">
        Přeskočit na obsah
      </a>

      <header className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="container nav-inner">
          <a className="brand" href="#top" aria-label="Trenigo – úvodní stránka">
            <LandingBrandLogo alt="Trenigo SportAkademie" className="brand-logo" />
          </a>
          <nav className="nav-links" aria-label="Hlavní navigace">
            <a href="#funkce">Funkce</a>
            <a href="#jak-to-funguje">Jak to funguje</a>
            <a href="#pro-koho">Pro koho</a>
            <a href="#faq">Časté otázky</a>
            <a href="#kontakt">Kontakt</a>
          </nav>
          <div className="nav-actions">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main id="obsah">
        <LandingLogoHero />

        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Software pro trenéry a sportovní akademie
              </span>
              <h1>
                Mějte akademii pod kontrolou.
                <br />
                <span className="gradient-text">A hlavu u tréninku.</span>
              </h1>
              <p className="hero-lede">
                Docházka, kredity, platby, tréninky i mzdy trenérů v jednom systému. Trenigo převezme každodenní
                administrativu, abyste nemuseli po večerech dohánět tabulky a ruční přepočty.
              </p>
              <div className="hero-actions">
                <a className="btn btn-primary" href={LOGIN_URL}>
                  Přihlásit se <span className="arrow">→</span>
                </a>
                <a className="btn btn-secondary" href="#funkce">
                  Podívat se, co Trenigo umí
                </a>
              </div>
            </div>

            <div className="app-shell" aria-label="Ukázka rozhraní Trenigo">
              <div className="app-window">
                <div className="window-bar">
                  <span className="dots">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="window-title">DASHBOARD AKADEMIE</span>
                  <span />
                </div>
                <div className="app-body">
                  <aside className="side" aria-hidden="true">
                    <div className="side-logo">T</div>
                    <div className="side-item active" />
                    <div className="side-item" />
                    <div className="side-item" />
                    <div className="side-item" />
                    <div className="side-item" />
                  </aside>
                  <div className="dashboard">
                    <div className="dash-head">
                      <div>
                        {/* FR01 20261005_PredpilotniAudit2_Opravy (FR-08): text ukázky, ne nadpis - jinak by
                            po h1 následoval h3 bez h2 (axe `heading-order`). */}
                        <p className="dash-greeting">Dobré ráno, Petře</p>
                        <p>Úterý 22. července · SportAkademie</p>
                      </div>
                      <div className="avatar" />
                    </div>
                    <div className="stat-grid">
                      <div className="stat">
                        <small>Dnešní tréninky</small>
                        <strong>8</strong>
                        <em>3 dokončené</em>
                      </div>
                      <div className="stat">
                        <small>Aktivní svěřenci</small>
                        <strong>64</strong>
                        <em>+4 tento měsíc</em>
                      </div>
                      <div className="stat">
                        <small>Nízký kredit</small>
                        <strong>5</strong>
                        <em>vyžaduje pozornost</em>
                      </div>
                    </div>
                    <div className="chart-card">
                      <div className="chart-head">
                        <span>Tréninky za posledních 7 dní</span>
                        <span>42 celkem</span>
                      </div>
                      <div className="bars" aria-hidden="true">
                        <span style={{ '--h': '45%', '--d': '.05s' } as CSSProperties} />
                        <span style={{ '--h': '72%', '--d': '.10s' } as CSSProperties} />
                        <span style={{ '--h': '56%', '--d': '.15s' } as CSSProperties} />
                        <span style={{ '--h': '90%', '--d': '.20s' } as CSSProperties} />
                        <span style={{ '--h': '68%', '--d': '.25s' } as CSSProperties} />
                        <span style={{ '--h': '82%', '--d': '.30s' } as CSSProperties} />
                        <span style={{ '--h': '60%', '--d': '.35s' } as CSSProperties} />
                      </div>
                    </div>
                    <div className="schedule">
                      <div className="session">
                        <span className="session-time">15:30</span>
                        <span className="session-name">
                          <strong>Tomáš Novák</strong>
                          <span>Individuální trénink</span>
                        </span>
                        <span className="badge">potvrzeno</span>
                      </div>
                      <div className="session">
                        <span className="session-time">17:00</span>
                        <span className="session-name">
                          <strong>Skupina U12</strong>
                          <span>6 svěřenců</span>
                        </span>
                        <span className="badge">naplánováno</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="float-card">
                <span>Vybrané kredity tento měsíc</span>
                <strong>76 % plánu</strong>
                <div className="progress">
                  <i />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="trust-strip" aria-label="Hlavní vlastnosti služby">
          <div className="container trust-inner">
            <div className="trust-label">Méně administrativy. Více času na trénink.</div>
            <div className="trust-item">
              <span className="trust-icon">✓</span> Automatické kredity
            </div>
            <div className="trust-item">
              <span className="trust-icon">↗</span> Přehled podle rolí
            </div>
            <div className="trust-item">
              <span className="trust-icon">⌁</span> Auditovatelná historie
            </div>
          </div>
        </section>

        <section className="section" id="funkce">
          <div className="container">
            <Reveal className="section-head">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Vše, co se řeší po tréninku
              </span>
              <h2>Trénování má být o lidech. Ne o tabulkách.</h2>
              <p>Zapíšete trénink a Trenigo propojí docházku, kredit i další agendu samo — bez ručního přepisování mezi evidencemi.</p>
            </Reveal>
            <div className="feature-grid">
              {FEATURES.map((feature) => (
                <Reveal as="article" className="glass-card" key={feature.title}>
                  <div className="icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt">
          <div className="container showcase">
            <Reveal className="showcase-copy">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Připravené do tělocvičny i kanceláře
              </span>
              <h2>Trenér zapisuje. Trenigo se postará o zbytek.</h2>
              <p>
                Docházku vyřešíte rovnou po tréninku z telefonu. Vedení akademie přitom okamžitě získá aktuální
                údaje bez dalšího přepisování.
              </p>
              <div className="benefit-list">
                {BENEFITS.map((benefit) => (
                  <div className="benefit" key={benefit.num}>
                    <span className="benefit-icon">{benefit.num}</span>
                    <div>
                      <strong>{benefit.title}</strong>
                      <span>{benefit.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="phone-wrap" aria-label="Mobilní ukázka portálu svěřence">
              <div className="orbit one">
                <span className="orbit-dot" />
              </div>
              <div className="orbit two">
                <span className="orbit-dot" />
              </div>
              <div className="phone">
                <div className="phone-screen">
                  <div className="mobile-top">
                    <strong>Můj přehled</strong>
                    <span className="mobile-avatar" />
                  </div>
                  <div className="credit-card">
                    <small>Aktuální kredit</small>
                    <strong>8</strong>
                    <div className="credit-meta">
                      <span>Balíček 10 tréninků</span>
                      <span>Aktivní</span>
                    </div>
                  </div>
                  <div className="mobile-heading">
                    <strong>Nejbližší tréninky</strong>
                    <span>Červenec</span>
                  </div>
                  <div className="mobile-session">
                    <span className="timebox">
                      23<br />ČT
                    </span>
                    <span>
                      <strong>Individuální trénink</strong>
                      <span>16:30 · Petr Novotný</span>
                    </span>
                    <span className="tick">✓</span>
                  </div>
                  <div className="mobile-session">
                    <span className="timebox">
                      25<br />SO
                    </span>
                    <span>
                      <strong>Skupinový trénink</strong>
                      <span>10:00 · Hala Slaný</span>
                    </span>
                    <span className="tick">✓</span>
                  </div>
                  <div className="mobile-heading">
                    <strong>Poslední aktivita</strong>
                    <span>Vše</span>
                  </div>
                  <div className="mobile-session">
                    <span className="timebox">+10</span>
                    <span>
                      <strong>Zakoupený balíček</strong>
                      <span>Platba převodem</span>
                    </span>
                    <span className="tick">✓</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="section" id="jak-to-funguje">
          <div className="container">
            <Reveal className="section-head">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Začít je jednoduché
              </span>
              <h2>Do provozu ve třech krocích.</h2>
              <p>Nemusíte zavádět složitý informační systém. Nastavíte akademii, přidáte lidi a můžete začít.</p>
            </Reveal>
            <div className="steps">
              {STEPS.map((step) => (
                <Reveal as="article" className="step" key={step.num}>
                  <span className="step-number">{step.num}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt" id="pro-koho">
          <div className="container">
            <Reveal className="section-head">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Od prvního svěřence po celou akademii
              </span>
              <h2>Trenigo roste společně s vámi.</h2>
              <p>Nemusíte měnit systém ve chvíli, kdy přiberete další trenéry nebo založíte nové skupiny.</p>
            </Reveal>
            <div className="roles">
              {ROLES.map((role) => (
                <Reveal as="article" className="role-card" key={role.label}>
                  <span className="role-label">{role.label}</span>
                  <h3>{role.title}</h3>
                  <p>{role.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="faq">
          <div className="container">
            <Reveal className="section-head">
              <span className="eyebrow">
                <span className="eyebrow-dot" /> Časté otázky
              </span>
              <h2>Co potřebujete vědět před vyzkoušením.</h2>
            </Reveal>
            <Reveal className="faq">
              {FAQ.map((item) => (
                <details key={item.q}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </Reveal>
          </div>
        </section>

        <section className="cta">
          <div className="container">
            <Reveal className="cta-panel">
              <div>
                <h2>Přestaňte počítat kredity v sešitě.</h2>
                <p>Podívejte se, jak může vypadat provoz akademie bez ručního počítání a zbytečného přepisování.</p>
              </div>
              <a
                className="btn"
                href="#kontakt"
                onClick={(event) => {
                  // Kliknutí s modifikátorem (nová karta) formulář na této stránce nepřepíná.
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  // Odskok a fokus řeší ContactSection sama: nativní skok na kotvu by fokus po kliknutí zrušil.
                  event.preventDefault();
                  window.history.replaceState(null, '', '#kontakt');
                  setDemoRequest((count) => count + 1);
                }}
              >
                {CONTACT_FORM_TYPE_LABELS.DEMO} <span className="arrow">→</span>
              </a>
            </Reveal>
          </div>
        </section>

        {/* FR02 20261001_LandingPage/002_Marketing: kontaktní formulář před patičkou (FR-10). */}
        <ContactSection demoRequest={demoRequest} />
      </main>

      <footer>
        <div className="container footer-inner">
          <div>
            <div className="footer-brand">
              <LandingBrandLogo alt="Trenigo SportAkademie" className="brand-logo" />
            </div>
            <span>© {year} Trenigo — aplikace pro trenéry a sportovní akademie.</span>
          </div>
          <div className="footer-links">
            <a href="#funkce">Funkce</a>
            <a href="#jak-to-funguje">Jak to funguje</a>
            <a href="/cookies">Cookies</a>
            <a href={LOGIN_URL}>Přihlásit se</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
